// Malawi — Nchombwa engine. 4 rows × 8 pits. 60 counters total.
// 2 seeds in every pit except the RIGHTMOST inner-row pit of each player (=0).
//
// Rules:
//   - Sow CCW around your own two rows only.
//   - Relay sowing: pick up an occupied landing pit and continue.
//   - Singleton restriction: can't start a 1-pit while any 2+ pit exists.
//     When only singletons remain: 1-pit moves only if next pit is empty.
//   - Capture: land in empty inner pit, opposite enemy inner pit occupied →
//     capture opposite inner pit (N seeds). Then capturer must remove N more
//     seeds from anywhere on opponent's two rows (player-chosen). All captured
//     counters leave play.
//   - Win: opponent has zero counters on board.
//   - Repeated board hash = draw.
//
// Note: the equal-bonus capture requires the UI to prompt the player to pick
// N additional pits. The engine exposes the move in two phases:
//   1. applyMove(state, r, c) → if capture, may return { state: 'AWAITING_ADDITIONAL', additionalAllowed, additionalPool, ... }
//   2. completeAdditionalCapture(state, picks) → final state
// For testability we also expose applyMoveWithPicks(state, r, c, picks).

export const RULES_VERSION = "2026.02.18";
export const RULESET_ID = "mw-nchombwa-standard";
export const ROWS = 4;
export const COLS = 8;
export const PLAYER_A = "A";
export const PLAYER_B = "B";

const ROUTE_A = [
  [3, 0], [3, 1], [3, 2], [3, 3], [3, 4], [3, 5], [3, 6], [3, 7],
  [2, 7], [2, 6], [2, 5], [2, 4], [2, 3], [2, 2], [2, 1], [2, 0],
];
const ROUTE_B = [
  [0, 7], [0, 6], [0, 5], [0, 4], [0, 3], [0, 2], [0, 1], [0, 0],
  [1, 0], [1, 1], [1, 2], [1, 3], [1, 4], [1, 5], [1, 6], [1, 7],
];
function routeFor(p) { return p === PLAYER_A ? ROUTE_A : ROUTE_B; }
function routeIndex(p, r, c) {
  const route = routeFor(p);
  for (let i = 0; i < route.length; i++) if (route[i][0] === r && route[i][1] === c) return i;
  return -1;
}
function ownsRow(p, row) { return p === PLAYER_A ? row === 2 || row === 3 : row === 0 || row === 1; }
function innerRow(p) { return p === PLAYER_A ? 2 : 1; }
function oppInner(p) { return p === PLAYER_A ? 1 : 2; }

function emptyBoard() { return Array.from({ length: ROWS }, () => Array(COLS).fill(0)); }
function cloneBoard(b) { return b.map((r) => r.slice()); }

export function createInitialState() {
  const board = emptyBoard();
  // 2 in every pit except rightmost inner pit of each player.
  // For Player A (rows 2 & 3), "right" from A's view is col 7 — that's A_inner col 7? But "rightmost"
  // is the rightmost on the route side. Per the brief: rightmost pit of each player's inner row empty.
  for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) board[r][c] = 2;
  // A's inner row is row 2; rightmost from A's view (A sits at bottom) = col 7.
  // B's inner row is row 1; rightmost from B's view (B sits at top, mirrored) = col 0.
  board[2][7] = 0;
  board[1][0] = 0;
  return {
    rulesetId: RULESET_ID,
    rulesVersion: RULES_VERSION,
    board,
    currentPlayer: PLAYER_A,
    phase: "NORMAL",
    captures: { A: 0, B: 0 },
    status: "ACTIVE",
    winner: null,
    endReason: null,
    turnNumber: 1,
    moveNumber: 0,
    history: [],
    pendingCapture: null, // { player, additional: number, pool: [[r,c],...] }
    positionCounts: {},
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

function ownPits(b, p) {
  const rows = p === PLAYER_A ? [2, 3] : [0, 1];
  const out = [];
  for (const r of rows) for (let c = 0; c < COLS; c++) if (b[r][c] > 0) out.push([r, c]);
  return out;
}
function countersOf(b, p) {
  const rows = p === PLAYER_A ? [2, 3] : [0, 1];
  let s = 0; for (const r of rows) for (let c = 0; c < COLS; c++) s += b[r][c]; return s;
}
function nextPitOnRoute(p, r, c) {
  const route = routeFor(p);
  const i = routeIndex(p, r, c);
  return route[(i + 1) % route.length];
}
function getPhase(b, p) {
  const own = ownPits(b, p);
  if (own.length === 0) return "NORMAL";
  return own.some(([r, c]) => b[r][c] >= 2) ? "NORMAL" : "SINGLETON";
}

export function getLegalMoves(state) {
  if (state.pendingCapture) return []; // must complete additional captures first
  const { board, currentPlayer } = state;
  const phase = getPhase(board, currentPlayer);
  const own = ownPits(board, currentPlayer);
  const legal = [];
  if (phase === "SINGLETON") {
    for (const [r, c] of own) {
      if (board[r][c] !== 1) continue;
      const [nr, nc] = nextPitOnRoute(currentPlayer, r, c);
      if (board[nr][nc] === 0) legal.push([r, c]);
    }
  } else {
    for (const [r, c] of own) if (board[r][c] >= 2) legal.push([r, c]);
  }
  return legal;
}

export function isLegalMove(state, r, c) {
  return getLegalMoves(state).some(([rr, cc]) => rr === r && cc === c);
}

// Helper: sow & relay until landing in empty pit.
function sowAndRelay(board, player, startR, startC) {
  const route = routeFor(player);
  let b = cloneBoard(board);
  let inHand = b[startR][startC];
  b[startR][startC] = 0;
  let idx = routeIndex(player, startR, startC);
  let last = [startR, startC];
  let relays = 0;
  let safety = 0;
  while (true) {
    while (inHand > 0) {
      idx = (idx + 1) % route.length;
      const [nr, nc] = route[idx];
      b[nr][nc] += 1;
      last = [nr, nc];
      inHand -= 1;
      if (++safety > 5000) throw new Error("SAFETY_BREAK");
    }
    if (b[last[0]][last[1]] === 1) break;
    inHand = b[last[0]][last[1]];
    b[last[0]][last[1]] = 0;
    relays += 1;
    idx = routeIndex(player, last[0], last[1]);
  }
  return { board: b, landing: last, relays };
}

// Phase 1: sow & resolve primary capture; if additional captures owed, set
// state.pendingCapture and return without ending turn.
export function applyMove(state, r, c) {
  if (state.status !== "ACTIVE") throw new Error("GAME_NOT_ACTIVE");
  if (!ownsRow(state.currentPlayer, r)) throw new Error("NOT_OWN_PIT");
  if (!isLegalMove(state, r, c)) throw new Error("ILLEGAL_MOVE");
  const player = state.currentPlayer;
  const opp = player === PLAYER_A ? PLAYER_B : PLAYER_A;

  const events = [];
  events.push({ type: "TURN_STARTED", player, turn: state.turnNumber });
  events.push({ type: "PIT_SELECTED", from: [r, c], count: state.board[r][c] });

  const { board: postBoard, landing, relays } = sowAndRelay(state.board, player, r, c);
  events.push({ type: "SOWING_COMPLETE", landing, relays });

  let board = postBoard;
  let capturedPrimary = 0;
  let pendingCapture = null;
  if (landing[0] === innerRow(player) && board[oppInner(player)][landing[1]] > 0) {
    const oppI = oppInner(player);
    const col = landing[1];
    const n = board[oppI][col];
    board = cloneBoard(board);
    board[oppI][col] = 0;
    capturedPrimary = n;
    events.push({ type: "CAPTURE_TRIGGERED", at: landing, captured: n });
    // Determine additional allowance & pool.
    const oppOnBoard = countersOf(board, opp);
    const allowance = Math.min(n, oppOnBoard); // can't take more than opponent has on board
    if (allowance > 0) {
      const pool = [];
      const oppRows = opp === PLAYER_A ? [2, 3] : [0, 1];
      for (const rr of oppRows) for (let cc = 0; cc < COLS; cc++) if (board[rr][cc] > 0) pool.push([rr, cc]);
      pendingCapture = { player, additional: allowance, pool, taken: [] };
      events.push({ type: "ADDITIONAL_CAPTURE_OWED", allowance, pool });
    }
  }

  const captures = { A: state.captures.A, B: state.captures.B };
  captures[player] += capturedPrimary;

  // If no additional capture owed, this move ends here; otherwise wait.
  if (pendingCapture) {
    // Same player keeps the turn until they complete the additional captures.
    const historyEntry = {
      player, from: [r, c], landingPit: landing, captured: capturedPrimary,
      relays, prevBoard: state.board, prevCaptures: state.captures, prevPhase: state.phase,
      prevCurrentPlayer: state.currentPlayer, prevStatus: state.status, prevWinner: state.winner,
      prevEndReason: state.endReason, prevTurnNumber: state.turnNumber, events,
      pendingCapture,
    };
    return {
      state: { ...state, board, captures, pendingCapture, history: [...state.history, historyEntry], moveNumber: state.moveNumber + 1, updatedAt: Date.now() },
      events,
    };
  }

  return finalize({ state, board, captures, events, player, opp, landing, fromR: r, fromC: c, capturedPrimary, relays });
}

// Apply an entire move including a pre-known set of additional capture picks.
// Convenience for tests and AI.
export function applyMoveWithPicks(state, r, c, picks = []) {
  let { state: s1 } = applyMove(state, r, c);
  if (s1.pendingCapture) {
    s1 = completeAdditionalCapture(s1, picks);
  }
  return { state: s1 };
}

export function completeAdditionalCapture(state, picks) {
  if (!state.pendingCapture) throw new Error("NO_PENDING_CAPTURE");
  const pending = state.pendingCapture;
  if (picks.length !== pending.additional) throw new Error("WRONG_PICK_COUNT");
  let board = cloneBoard(state.board);
  let extra = 0;
  for (const [pr, pc] of picks) {
    const inPool = pending.pool.some(([rr, cc]) => rr === pr && cc === pc);
    if (!inPool) throw new Error("PICK_NOT_IN_POOL");
    if (board[pr][pc] === 0) throw new Error("PICK_ALREADY_EMPTY");
    extra += board[pr][pc];
    board[pr][pc] = 0;
  }
  const events = [{ type: "ADDITIONAL_CAPTURES_TAKEN", picks, total: extra }];
  const captures = { ...state.captures };
  captures[pending.player] += extra;
  const opp = pending.player === PLAYER_A ? PLAYER_B : PLAYER_A;
  const player = pending.player;
  return finalize({
    state: { ...state, pendingCapture: null }, board, captures, events,
    player, opp, landing: state.history[state.history.length - 1].landingPit,
    fromR: -1, fromC: -1, capturedPrimary: state.history[state.history.length - 1].captured + extra, relays: 0,
  });
}

function finalize({ state, board, captures, events, player, opp, landing, capturedPrimary, relays }) {
  let status = "ACTIVE", winner = null, endReason = null;
  if (countersOf(board, opp) === 0) {
    status = "WON"; winner = player; endReason = "OPPONENT_CLEARED";
    events.push({ type: "GAME_WON", player, reason: endReason });
  }
  const nextPlayer = opp;
  if (status === "ACTIVE") {
    const probe = { ...state, board, currentPlayer: nextPlayer, pendingCapture: null };
    if (getLegalMoves(probe).length === 0) {
      status = "WON"; winner = player; endReason = "OPPONENT_NO_LEGAL_MOVE";
      events.push({ type: "GAME_WON", player, reason: endReason });
    }
  }
  // Repetition detection (3-fold).
  const positionCounts = { ...(state.positionCounts || {}) };
  if (status === "ACTIVE") {
    const hash = JSON.stringify(board) + ":" + nextPlayer;
    positionCounts[hash] = (positionCounts[hash] || 0) + 1;
    if (positionCounts[hash] >= 3) {
      status = "DRAWN"; endReason = "REPETITION_DRAW";
      events.push({ type: "GAME_DRAWN", reason: endReason });
    }
  }
  events.push({ type: "TURN_ENDED", player, landingPit: landing, captured: capturedPrimary, relays });
  return {
    state: {
      ...state,
      board, currentPlayer: status === "ACTIVE" ? nextPlayer : state.currentPlayer,
      captures, status, winner, endReason, pendingCapture: null, positionCounts,
      turnNumber: status === "ACTIVE" ? state.turnNumber + 1 : state.turnNumber,
      updatedAt: Date.now(),
    },
    events,
  };
}

export function undoMove(state) {
  if (!state.history.length) return state;
  const last = state.history[state.history.length - 1];
  return {
    ...state,
    board: last.prevBoard,
    currentPlayer: last.prevCurrentPlayer,
    phase: last.prevPhase,
    captures: last.prevCaptures,
    status: last.prevStatus,
    winner: last.prevWinner,
    endReason: last.prevEndReason,
    turnNumber: last.prevTurnNumber,
    pendingCapture: null,
    moveNumber: Math.max(0, state.moveNumber - 1),
    history: state.history.slice(0, -1),
    updatedAt: Date.now(),
  };
}

export function totalCounters(state) {
  let s = state.captures.A + state.captures.B;
  for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) s += state.board[r][c];
  return s;
}

export function describeMove(entry) {
  if (!entry) return "";
  const p = entry.player === PLAYER_A ? "Player 1" : "Player 2";
  const parts = [`${p} sowed counter-clockwise around their two rows.`];
  if (entry.captured > 0) parts.push(`Captured ${entry.captured} seeds (incl. additional pits).`);
  else parts.push("No capture.");
  return parts.join(" ");
}
